import React from 'react';
import c from 'classnames';

import { useTranslation } from '@lyearn/i18n';
import { ArrowLeftOutline, ArrowRightFill, NormalText } from '@lyearn/icons';
import { useReactNodeView } from '@lyearn/lygo-core';
import { Box, Stack, Tooltip } from '@lyearn/ui';

import { FLASH_CARDS_BY_KEY } from '../../../consts';
import AddImageButton from './AddImageButton';
import {
  colorStyles,
  descriptionStyles,
  flashCardImageStyles,
  flashCardStyles,
  headerStyles,
  iconBorderStyles,
  iconHoverStyles,
  iconStyles,
  textAreaStyles,
} from './styles';
import FlashCardTextArea from './TextArea';
import { FlashCardProps } from './types';
import useUpdateCard from './useUpdateCard';

export const IMAGE_BACKGROUND_COLORS = {
  color: '#FFFFFF',
  icon: '#FFFFFF',
  background: '#FFFFFF',
  iconHover: 'rgba(191, 191, 191, 0.3)',
};

const FlashCard: React.FC<FlashCardProps> = (props) => {
  const { card, side, type, readOnly, toggleCard, className } = props;
  const cardProps = React.useMemo(() => card?.[side], [card, side]);
  const colors = React.useMemo(
    () => (cardProps?.backgroundImgSrc ? IMAGE_BACKGROUND_COLORS : FLASH_CARDS_BY_KEY[type][side]),
    [cardProps?.backgroundImgSrc, side, type],
  );
  const NavigationIcon = React.useMemo(
    () => (side === 'front' ? ArrowRightFill : ArrowLeftOutline),
    [side],
  );
  const { updateAttrs } = useReactNodeView();
  const { t } = useTranslation();
  const { onHeadingChange, onDescriptionChange, toggleHeadingDisplay, onImageChange } =
    useUpdateCard({
      updateAttrs,
      card,
      side,
    });

  return (
    <Stack
      className={c(
        className,
        cardProps?.backgroundImgSrc
          ? flashCardImageStyles(cardProps?.backgroundImgSrc)
          : flashCardStyles(colors.background),
        'relative h-full rounded px-24 pt-32 pb-16',
      )}
      direction="col">
      {cardProps?.showHeading ? (
        <FlashCardTextArea
          charLimit={30}
          className={c('heading-m mb-8 border-none', textAreaStyles(colors.color), headerStyles)}
          lineHeight={30}
          lineLimit={2}
          placeholder={t('Lygo:FlashCard.HeadingPlaceholder')}
          readOnly={readOnly}
          text={cardProps.heading}
          onTextChange={onHeadingChange}
        />
      ) : null}
      <FlashCardTextArea
        charLimit={140}
        className={c(descriptionStyles, textAreaStyles(colors.color), 'border-none')}
        lineHeight={27}
        lineLimit={6}
        placeholder={t('Lygo:FlashCard.DescriptionPlaceholder')}
        readOnly={readOnly}
        text={cardProps?.description}
        onTextChange={onDescriptionChange}
      />
      {readOnly ? null : (
        <Stack alignItems="center" className="absolute bottom-16 start-16">
          <Tooltip placement="top" title={t('Lygo:FlashCard.HeadingTooltip')!}>
            <Box
              className={c(
                iconStyles(!!cardProps?.showHeading),
                iconHoverStyles(colors.iconHover),
                'flex h-40 w-40 items-center justify-center rounded-8',
              )}
              role="button"
              onClick={toggleHeadingDisplay}>
              <NormalText className={c('h-24 w-24', colorStyles(colors.icon))} />
            </Box>
          </Tooltip>
          <AddImageButton
            backgroundImgSrc={cardProps?.backgroundImgSrc}
            iconColor={colors.icon}
            iconHoverColor={colors.iconHover}
            onImageChange={onImageChange}
          />
        </Stack>
      )}
      <Tooltip placement="top" title={t('Lygo:FlashCard.FlipTooltip')!}>
        <Stack
          alignItems="center"
          className={c(
            'absolute bottom-16 h-40 w-40 rounded-full end-16',
            iconBorderStyles(colors.icon),
            iconHoverStyles(colors.iconHover),
          )}
          justifyContent="center"
          role="button"
          onClick={toggleCard}>
          <NavigationIcon className={c('h-20 w-20', colorStyles(colors.icon))} />
        </Stack>
      </Tooltip>
    </Stack>
  );
};

export default FlashCard;
